import { BLOCKS, MARKS } from '@contentful/rich-text-types'
import {
  AssetOptionsContext,
  type AssetOptionsContextProps,
} from '@contexts/asset-options'
import { ContentType } from '@entries/content-types'
import ContentfulAsset from '@entries/contentful-asset'
import { withDisplayName } from '@hocs/with-display-name'
import { boldResponsive } from '@render-mark/bold-responsive'
import { codeGray } from '@render-mark/code-gray'
import { codeSmallLineHeight } from '@render-mark/code-small-line-height'
import { italicResponsive } from '@render-mark/italic-responsive'
import { documentCentered } from '@render-node/document-centered'
import { documentHeading3Green } from '@render-node/document-heading-3-green'
import { heading3GreenSpanInherit } from '@render-node/heading-3-green-span-inherit'
import { heading3SpanBold } from '@render-node/heading-3-span-bold'
import { paragraphBody2 } from '@render-node/paragraph-body-2'
import { children } from '@render-node/utils/children'
import { Entry } from '@resolvers/entry'
import RichText from '@resolvers/rich-text'
import { defaultRichTextFieldOptionsDeprecated } from '@resolvers/rich-text/default-rich-text-field-options-deprecated'
import { Divider } from '@ui/atoms/Divider'
import {
  EnhancedButton,
  EnhancedLink,
} from '@ui/enhanced-components/enhanced-components'
import {
  ENHANCED_COMPONENT_ROLE,
  ENHANCED_COMPONENT_TYPE,
} from '@ui/enhanced-components/types'
import { graphql } from 'gatsby'
import React, { Fragment } from 'react'

const assetOptions: AssetOptionsContextProps = {
  maxHeight: '8.625rem',
  maxSvgHeight: '8.625rem',
  objectFit: 'contain',
  maxWidth: '100%',
}

const ContentfulContentArticleCardOffer: Utils.EntryFC<
  Queries.ContentfulContentArticleCardOfferFragment
> = ({
  content: {
    contentArticleTitle: title,
    subtitle,
    image,
    imageText,
    content,
    contentLinkText,
    contentLinkUrl,
  },
  fieldInspectorProps,
}) => {
  const hasHeader = title || subtitle || image || imageText
  const hasFooterLink = contentLinkText && contentLinkUrl
  const LinkComponent = hasFooterLink ? EnhancedLink : Fragment
  const clickableProps = hasFooterLink
    ? {
        href: contentLinkUrl,
        className: 'flex w-full cursor-pointer',
      }
    : {}

  return (
    <div className="relative flex h-full w-full flex-col rounded-md border border-solid border-theme-default-accent2 bg-background-default text-text-default shadow-[0_5px_10px_0_rgb(35,35,35,0.06)]">
      <div className="flex h-full flex-col justify-between p-10">
        <div>
          {hasHeader && (
            <header className="text-center">
              <div className="h-auto overflow-hidden laptop:h-[5rem]">
                {title && (
                  <RichText
                    {...fieldInspectorProps({
                      fieldId: 'title',
                    })}
                    content={title}
                    options={{
                      ...defaultRichTextFieldOptionsDeprecated,
                      renderNode: {
                        ...defaultRichTextFieldOptionsDeprecated.renderNode,
                        [BLOCKS.DOCUMENT]: documentHeading3Green,
                        [BLOCKS.HEADING_3]: heading3GreenSpanInherit,
                        [BLOCKS.PARAGRAPH]: children,
                      },
                    }}
                  />
                )}
                {subtitle && (
                  <RichText
                    {...fieldInspectorProps({
                      fieldId: 'subtitle',
                    })}
                    content={subtitle}
                    options={{
                      ...defaultRichTextFieldOptionsDeprecated,
                      renderNode: {
                        ...defaultRichTextFieldOptionsDeprecated.renderNode,
                        [BLOCKS.HEADING_3]: heading3SpanBold,
                      },
                    }}
                  />
                )}
              </div>
              {image && (
                <div
                  className="pb-10"
                  {...fieldInspectorProps({
                    fieldId: 'image',
                  })}
                >
                  <LinkComponent {...clickableProps}>
                    <AssetOptionsContext.Provider value={assetOptions}>
                      <Entry
                        content={image}
                        mapping={{
                          [ContentType.ContentfulAsset]: ContentfulAsset,
                        }}
                      />
                    </AssetOptionsContext.Provider>
                  </LinkComponent>
                </div>
              )}
              {imageText && (
                <div className="pb-10">
                  <RichText
                    {...fieldInspectorProps({
                      fieldId: 'imageText',
                    })}
                    content={imageText}
                    options={{
                      ...defaultRichTextFieldOptionsDeprecated,
                      renderMark: {
                        ...defaultRichTextFieldOptionsDeprecated.renderMark,
                        [MARKS.ITALIC]: italicResponsive,
                        [MARKS.BOLD]: boldResponsive,
                        [MARKS.CODE]: codeSmallLineHeight,
                      },
                      renderNode: {
                        ...defaultRichTextFieldOptionsDeprecated.renderNode,
                        [BLOCKS.DOCUMENT]: documentCentered,
                        [BLOCKS.PARAGRAPH]: paragraphBody2,
                      },
                    }}
                  />
                </div>
              )}
            </header>
          )}
          {content && (
            <>
              <Divider />
              <div className="grow self-stretch pt-10">
                <RichText
                  {...fieldInspectorProps({
                    fieldId: 'content',
                  })}
                  content={content}
                  options={{
                    ...defaultRichTextFieldOptionsDeprecated,
                    renderMark: {
                      ...defaultRichTextFieldOptionsDeprecated.renderMark,
                      [MARKS.CODE]: codeGray,
                    },
                  }}
                />
              </div>
            </>
          )}
        </div>
        {hasFooterLink && (
          <div className="pt-10">
            <EnhancedButton
              className="!m-auto !flex !min-h-[40px] !w-full !max-w-[334px] !px-8 !py-0 [&>div]:overflow-visible"
              data-role={ENHANCED_COMPONENT_ROLE.SECONDARY}
              data-type={ENHANCED_COMPONENT_TYPE.CTA}
              href={contentLinkUrl}
              variant="secondary"
              {...fieldInspectorProps({
                fieldId: 'contentLinkText',
              })}
            >
              {contentLinkText}
            </EnhancedButton>
          </div>
        )}
      </div>
    </div>
  )
}

export const query = graphql`
  fragment ContentfulContentArticleCardOffer on Contentful4TmD9EihHakmKMkEwKaUwQ {
    ...ContentfulEntry
    contentArticleTitle: title {
      ...ContentfulContentArticleTitle
    }
    subtitle {
      ...ContentfulContentArticleSubtitle
    }
    content {
      ...ContentfulContentArticleContent
    }
    image {
      ...ContentfulDefaultAsset
    }
    imageText {
      ...ContentfulContentArticleImageText
    }
    contentLinkText
    contentLinkUrl
  }
`

export default withDisplayName(
  ContentfulContentArticleCardOffer,
  'ContentfulContentArticleCardOffer'
)
